/**
 * BrainPlayground — A/B Testing panel for comparing Brain Engine presets
 * Executes the same query with two different presets in parallel and shows results side by side.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../../common/contexts/ProjectContext";
import { useTags } from "../../common/contexts/TagsContext";
import { socketService } from "../../../services/socket";
import { ParticipantBadge } from "../../common/components/ParticipantBadge";
import { isColorLight } from "../../../utils/participantColors";
import "../../discovery/views/Discovery.css";
import { authFetch } from "../../../services/authFetch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StarRating } from "@/components/ui/star-rating";

interface Preset {
  id: string;
  name: string;
  is_active: boolean;
}

interface BenchmarkSummary {
  id: string;
  query: string;
  preset_a_id: string;
  preset_b_id: string;
  rating_a: number | null;
  rating_b: number | null;
  created_by: string;
  created_at: string;
}

interface TestCase {
  id: string;
  query: string;
  expected_keywords: string[];
  category: string;
  filters?: {
    rundownIds?: string[];
    eventIds?: string[];
    storyIds?: string[];
    participantIds?: string[];
    selectAll?: boolean;
    dataSources?: { logs: boolean; transcripts: boolean; otfs?: boolean };
  };
  created_at: string;
}

interface PipelineResult {
  answer?: string;
  citations?: any[];
  metadata?: {
    cost?: number;
    inputTokens?: number;
    outputTokens?: number;
    responseTimeMs?: number;
    stories?: number;
    searchPipeline?: {
      mode?: string;
      storiesUsed?: number;
      totalStoriesAvailable?: number;
      steps?: Array<{ ts: number; msg: string }>;
    };
    components?: { logs?: number; transcriptions?: number };
    contextDebug?: {
      storiesIncluded?: { id: string; title: string; isOTF?: boolean }[];
      storiesExcluded?: { id: string; title: string }[];
      logCount?: number;
      transcriptCount?: number;
      contextSizeKB?: number;
      contextTokens?: number;
      contextMode?: string;
      systemPromptLength?: number;
      contextPreview?: string;
    };
  };
  error?: string;
}

// ─── Styles ───
const panelBg = "var(--color-panel-bg)";
const panelBorder = "1px solid var(--color-panel-border)";
const brandColor = "var(--color-brand, #E53E3E)";
const brandRgb = "var(--color-brand-rgb, 229, 62, 62)";

const cardStyle: React.CSSProperties = {
  background: panelBg,
  border: panelBorder,
  borderRadius: "12px",
  padding: "16px",
};

export const BrainPlayground: React.FC = () => {
  const { t } = useTranslation();
  const { activeProject } = useProjects();
  const [presets, setPresets] = useState<Preset[]>([]);
  const [presetIdA, setPresetIdA] = useState("");
  const [presetIdB, setPresetIdB] = useState("");
  const [query, setQuery] = useState("");
  const [running, setRunning] = useState(false);
  const [resultA, setResultA] = useState<PipelineResult | null>(null);
  const [resultB, setResultB] = useState<PipelineResult | null>(null);
  const [totalTimeMs, setTotalTimeMs] = useState<number | null>(null);
  const [benchmarkId, setBenchmarkId] = useState<string | null>(null);
  const [benchmarks, setBenchmarks] = useState<BenchmarkSummary[]>([]);
  const [ratingA, setRatingA] = useState<number | null>(null);
  const [ratingB, setRatingB] = useState<number | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [statusA, setStatusA] = useState<string[]>([]);
  const [statusB, setStatusB] = useState<string[]>([]);
  const [comparisonMode, setComparisonMode] = useState<"single" | "dual">("single");
  const isSingle = comparisonMode === "single";
  const activeStreamsRef = useRef<EventSource[]>([]);

  // Filter state (same as Discovery)
  const [filtersData, setFiltersData] = useState<{
    rundowns: any[];
    events: any[];
    stories: any[];
    participants: any[];
    tags: any[];
  }>({ rundowns: [], events: [], stories: [], participants: [], tags: [] });
  const [activeFilters, setActiveFilters] = useState<{
    rundownIds: string[];
    eventIds: string[];
    storyIds: string[];
    participantIds: string[];
    tags: string[];
  }>({ rundownIds: [], eventIds: [], storyIds: [], participantIds: [], tags: [] });
  const [showFilters, setShowFilters] = useState(true);
  const [selectAll, setSelectAll] = useState(true);
  const [dataSources, setDataSources] = useState<{
    logs: boolean;
    transcripts: boolean;
    otfs: boolean;
  }>({ logs: true, transcripts: true, otfs: true });
  const [expandedRundownIds, setExpandedRundownIds] = useState<string[]>([]);
  const [expandedEventIds, setExpandedEventIds] = useState<string[]>([]);
  const [inspectorOpenA, setInspectorOpenA] = useState(false);
  const [inspectorOpenB, setInspectorOpenB] = useState(false);
  const [showPreviewDrawer, setShowPreviewDrawer] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [previewTokens, setPreviewTokens] = useState<number | null>(null);

  // Citation popup state
  const [activeCitation, setActiveCitation] = useState<{
    citation: any;
    x: number;
    y: number;
  } | null>(null);

  // Load presets
  const loadPresets = useCallback(async () => {
    if (!activeProject) return;
    try {
      const res = await authFetch(`/api/brain/presets?projectId=${activeProject.id}`);
      const data = await res.json();
      setPresets(data.presets || []);
      // Default: first two presets
      if (data.presets?.length >= 2 && !presetIdA && !presetIdB) {
        setPresetIdA(data.presets[0].id);
        setPresetIdB(data.presets[1].id);
      } else if (data.presets?.length === 1 && !presetIdA) {
        setPresetIdA(data.presets[0].id);
        setPresetIdB(data.presets[0].id);
      }
    } catch (e) {
      console.error("Failed to load presets", e);
    }
  }, [activeProject]);

  // Load benchmark history
  const loadBenchmarks = useCallback(async () => {
    if (!activeProject) return;
    try {
      const res = await authFetch(`/api/brain/benchmarks?projectId=${activeProject.id}`);
      const data = await res.json();
      setBenchmarks(data.benchmarks || []);
    } catch (e) {
      console.error("Failed to load benchmarks", e);
    }
  }, [activeProject]);

  // Load test cases
  const loadTestCases = useCallback(async () => {
    if (!activeProject) return;
    try {
      const res = await authFetch(`/api/brain/test-cases?projectId=${activeProject.id}`);
      const data = await res.json();
      setTestCases(data.testCases || []);
    } catch (e) {
      console.error("Failed to load test cases", e);
    }
  }, [activeProject]);
  useEffect(() => {
    loadPresets();
    loadBenchmarks();
    loadTestCases();
  }, [loadPresets, loadBenchmarks, loadTestCases]);

  // Listen to test case updates from BrainTestSuite
  useEffect(() => {
    const handleUpdate = () => loadTestCases();
    window.addEventListener("test-cases-updated", handleUpdate);
    return () => window.removeEventListener("test-cases-updated", handleUpdate);
  }, [loadTestCases]);

  // Load filter data when project changes
  useEffect(() => {
    if (!activeProject) return;
    const handler = (data: any) => setFiltersData(data);
    socketService.on("ai:filters-data", handler);
    socketService.emit("ai:get-filters-data", { projectId: activeProject.id });
    return () => {
      socketService.off("ai:filters-data", handler);
    };
  }, [activeProject]);

  const toggleFilter = (type: keyof typeof activeFilters, id: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [type]: prev[type].includes(id) ? prev[type].filter((x) => x !== id) : [...prev[type], id],
    }));
  };
  const toggleRundownExpand = (id: string) =>
    setExpandedRundownIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const toggleEventExpand = (id: string) =>
    setExpandedEventIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  // Run playground comparison
  const runComparison = async () => {
    if (!query.trim() || !presetIdA || !activeProject) return;
    if (!isSingle && !presetIdB) return;
    setRunning(true);
    setResultA(null);
    setResultB(null);
    setStatusA([]);
    setStatusB([]);
    setTotalTimeMs(null);
    setBenchmarkId(null);
    setRatingA(null);
    setRatingB(null);
    setStatusMsg(
      isSingle
        ? t("admin.brainPlayground.preparingExecution")
        : t("admin.brainPlayground.preparingComparison"),
    );

    const startTime = Date.now();
    const filtersPayload = {
      selectAll,
      rundownIds: activeFilters.rundownIds,
      eventIds: activeFilters.eventIds,
      storyIds: activeFilters.storyIds,
      participantIds: activeFilters.participantIds,
      tags: activeFilters.tags,
      dataSources,
    };

    try {
      // Get auth token for Brain API security
      const { supabase } = await import("../../../services/supabase");
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const authToken = session?.access_token;
      const authHeaders = {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      };

      // 1. Prepare stream A
      const resA = await authFetch("/api/chat/brain/prepare", {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          query: query.trim(),
          projectId: activeProject.id,
          _presetOverrideId: presetIdA,
          filters: filtersPayload,
        }),
      });
      if (!resA.ok) {
        const body = await resA.text().catch(() => "");
        throw new Error(`Error ${resA.status}: ${body || "Sin respuesta del servidor"}`);
      }
      const dataA = await resA.json();
      if (dataA.error) throw new Error(`Preset A error: ${dataA.error}`);

      let dataB: any = null;
      if (!isSingle) {
        // 2. Prepare stream B
        const resB = await authFetch("/api/chat/brain/prepare", {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            query: query.trim(),
            projectId: activeProject.id,
            _presetOverrideId: presetIdB,
            filters: filtersPayload,
          }),
        });
        if (!resB.ok) {
          const body = await resB.text().catch(() => "");
          throw new Error(`Error ${resB.status}: ${body || "Sin respuesta del servidor"}`);
        }
        dataB = await resB.json();
        if (dataB.error) throw new Error(`Preset B error: ${dataB.error}`);
      }

      setStatusMsg(
        isSingle
          ? t("admin.brainPlayground.executingPipeline")
          : t("admin.brainPlayground.executingParallel"),
      );

      // 3. Connect EventSources
      let doneA = false;
      let doneB = isSingle ? true : false;
      let finalResultA: PipelineResult | null = null;
      let finalResultB: PipelineResult | null = null;

      const checkFinished = async () => {
        if (doneA && doneB) {
          setRunning(false);
          setStatusMsg(null);
          setTotalTimeMs(Date.now() - startTime);

          // Save benchmark
          try {
            const bRes = await authFetch("/api/brain/benchmarks", {
              method: "POST",
              headers: authHeaders,
              body: JSON.stringify({
                projectId: activeProject.id,
                query: query.trim(),
                presetIdA,
                presetIdB,
                resultA: finalResultA || { error: "Failed" },
                resultB: finalResultB || { error: "Failed" },
              }),
            });
            const bData = await bRes.json();
            if (bData.benchmarkId) setBenchmarkId(bData.benchmarkId);
            loadBenchmarks(); // refresh history
          } catch (err) {
            console.error("Failed to save benchmark", err);
          }
        }
      };

      const runStream = (streamId: string, side: "A" | "B") => {
        return new Promise<void>((resolve) => {
          const evtSource = new EventSource(`/api/chat/brain/events/${streamId}`);
          activeStreamsRef.current.push(evtSource);

          let answer = "";

          evtSource.addEventListener("status", (e) => {
            try {
              const data = JSON.parse(e.data);
              if (data.message) {
                if (side === "A") setStatusA((prev) => [...prev, data.message]);
                else setStatusB((prev) => [...prev, data.message]);
              }
            } catch {}
          });

          evtSource.addEventListener("chunk", (e) => {
            try {
              const data = JSON.parse(e.data);
              answer += data.text || data.chunk || "";
              if (side === "A") setResultA({ answer });
              else setResultB({ answer });
            } catch {}
          });

          evtSource.addEventListener("done", (e) => {
            try {
              const data = JSON.parse(e.data);
              const result = {
                answer: data.fullText || answer,
                citations: data.citations || [],
                metadata: data.metadata || {},
              };
              if (side === "A") {
                finalResultA = result;
                setResultA(result);
                doneA = true;
              } else {
                finalResultB = result;
                setResultB(result);
                doneB = true;
              }
            } catch {
              if (side === "A") doneA = true;
              else doneB = true;
            }
            evtSource.close();
            checkFinished();
            resolve();
          });

          evtSource.addEventListener("error", (e: any) => {
            let errMsg = "Stream error";
            try {
              if (e.data) {
                const data = JSON.parse(e.data);
                errMsg = data.message || data.error || errMsg;
              }
            } catch {}

            if (side === "A") {
              finalResultA = { error: errMsg };
              setResultA(finalResultA);
              doneA = true;
            } else {
              finalResultB = { error: errMsg };
              setResultB(finalResultB);
              doneB = true;
            }
            evtSource.close();
            checkFinished();
            resolve();
          });

          // Handle native EventSource connection errors (no data)
          evtSource.onerror = () => {
            if (evtSource.readyState === EventSource.CLOSED) {
              // Only treat as error if we don't have answer content yet
              if (!answer) {
                const connErr = t("admin.brainPlayground.connectionLost");
                if (side === "A" && !doneA) {
                  finalResultA = { error: connErr };
                  setResultA(finalResultA);
                  doneA = true;
                } else if (side === "B" && !doneB) {
                  finalResultB = { error: connErr };
                  setResultB(finalResultB);
                  doneB = true;
                }
                evtSource.close();
                checkFinished();
                resolve();
              }
            }
          };
        });
      };

      runStream(dataA.streamId, "A");
      if (!isSingle && dataB) runStream(dataB.streamId, "B");
    } catch (err: any) {
      setStatusMsg(`Error: ${err.message}`);
      setRunning(false);
    }
  };

  // Update rating
  const updateRating = async (side: "a" | "b", rating: number) => {
    if (!benchmarkId) return;
    if (side === "a") setRatingA(rating);
    else setRatingB(rating);

    try {
      await authFetch(`/api/brain/benchmarks/${benchmarkId}/rating`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(side === "a" ? { rating_a: rating } : { rating_b: rating }),
      });
    } catch (e) {
      console.error("Failed to save rating", e);
    }
  };

  // Load a saved benchmark
  const loadBenchmark = async (id: string) => {
    try {
      const res = await authFetch(`/api/brain/benchmarks/${id}`);
      const data = await res.json();
      if (data.benchmark) {
        const b = data.benchmark;
        setQuery(b.query);
        setPresetIdA(b.preset_a_id);
        setPresetIdB(b.preset_b_id);
        setResultA(b.result_a);
        setResultB(b.result_b);
        setRatingA(b.rating_a);
        setRatingB(b.rating_b);
        setBenchmarkId(b.id);
        setTotalTimeMs(null);
      }
    } catch (e) {
      console.error("Failed to load benchmark", e);
    }
  };

  // Delete a benchmark from history
  const deleteBenchmark = async (id: string) => {
    try {
      await authFetch(`/api/brain/benchmarks/${id}`, { method: "DELETE" });
      if (benchmarkId === id) {
        setBenchmarkId(null);
        setResultA(null);
        setResultB(null);
      }
      loadBenchmarks();
    } catch (e) {
      console.error("Failed to delete benchmark", e);
    }
  };

  const getPresetName = (id: string) => presets.find((p) => p.id === id)?.name || id;

  // Load Preview
  const handleLoadPreview = async () => {
    if (!activeProject) return;
    setPreviewLoading(true);
    setShowPreviewDrawer(true);
    setPreviewContent(null);
    setPreviewTokens(null);

    // Simularemos o reusaremos la lógica de un benchmark rápido sin LLM,
    // pero dado que no tenemos el endpoint expuesto directamente como 'estimate-context'
    // emitimos el evento de chat con un flag especial o consultaremos el socket general.
    // Asumiendo que existe el evento 'ai/chat/estimate' o podemos enviar un test "dummy"
    // Para P100 usaremos el endpoint de estimate como en Discovery si existe,
    // sino emularemos un mensaje para ver el debug info.
    try {
      const rawBody = {
        query: "TEST_CONTEXT_PREVIEW_ONLY",
        projectId: activeProject.id,
        presetId: presetIdA, // Use preset A for context estimation
        filters: {
          selectAll,
          rundownIds: activeFilters.rundownIds,
          eventIds: activeFilters.eventIds,
          storyIds: activeFilters.storyIds,
          participantIds: activeFilters.participantIds,
          tags: activeFilters.tags,
          dataSources,
        },
      };
      const response = await authFetch("/api/brain/estimate-context", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rawBody),
      });
      const data = await response.json();
      if (data.contextPreview) {
        setPreviewContent(data.contextPreview);
        setPreviewTokens(data.contextTokens);
      } else {
        setPreviewContent(t("admin.brainPlayground.contextPreviewError"));
      }
    } catch (error) {
      console.error("Failed to load preview", error);
      setPreviewContent(t("admin.brainPlayground.contextPreviewError"));
    } finally {
      setPreviewLoading(false);
    }
  };

  // ─── Render formatted markdown answer ───
  const renderFormattedAnswer = (text: string, citations?: any[]) => {
    const citationMap = new Map<string, any>();
    const sequentialMap = new Map<string, number>();
    (citations || []).forEach((c: any, index: number) => {
      if (c.srcKey) {
        citationMap.set(c.srcKey, c);
        if (!sequentialMap.has(c.srcKey)) sequentialMap.set(c.srcKey, index + 1);
      }
    });

    const lines = text.split("\n");
    const elements: React.ReactNode[] = [];

    lines.forEach((line, lineIdx) => {
      const trimmed = line.trim();
      if (!trimmed) {
        elements.push(<br key={`br-${lineIdx}`} />);
        return;
      }

      // ### Heading
      const h3Match = trimmed.match(/^###\s+(.+)$/);
      if (h3Match) {
        elements.push(
          <h3
            key={`h3-${lineIdx}`}
            style={{
              color: brandColor,
              margin: "20px 0 10px 0",
              fontSize: "1rem",
              fontWeight: 700,
              borderBottom: `1px solid rgba(${brandRgb}, 0.3)`,
              paddingBottom: "6px",
              letterSpacing: "0.02em",
            }}
          >
            {h3Match[1]}
          </h3>,
        );
        return;
      }
      // ## Heading
      const h2Match = trimmed.match(/^##\s+(.+)$/);
      if (h2Match) {
        elements.push(
          <h2
            key={`h2-${lineIdx}`}
            style={{
              color: brandColor,
              margin: "24px 0 12px 0",
              fontSize: "1.1rem",
              fontWeight: 700,
              borderBottom: `2px solid rgba(${brandRgb}, 0.4)`,
              paddingBottom: "8px",
            }}
          >
            {h2Match[1]}
          </h2>,
        );
        return;
      }
      // **• Category Header**
      const headerMatch = trimmed.match(/^\*\*•\s*(.+?)\*\*$/);
      if (headerMatch) {
        elements.push(
          <h4
            key={`h-${lineIdx}`}
            style={{
              color: brandColor,
              margin: "16px 0 8px 0",
              fontSize: "0.9rem",
              fontWeight: 600,
              borderBottom: `1px solid rgba(${brandRgb}, 0.2)`,
              paddingBottom: "4px",
            }}
          >
            {headerMatch[1]}
          </h4>,
        );
        return;
      }

      // Bullet points
      const bulletMatch = trimmed.match(/^([○■●]|\*\s+|-\s+)\s*(.+)$/);
      const isBullet = bulletMatch !== null;
      const lineContent = isBullet ? bulletMatch![2] : trimmed;

      // Parse inline formatting
      let processed = lineContent
        .replace(/\[([^\]]*?)(LOG-\d+)\]/g, "[$2]")
        .replace(/\[([^\]]*?)(TRANS-\d+)\]/g, "[$2]")
        .replace(/\s*\[\d{1,2}:\d{2}(?::\d{2})?\]/g, "")
        .replace(/\[((?:(?:LOG|TRANS)-\d+,?\s*)+)\]/g, (match) => {
          const inner = match.slice(1, -1);
          return inner
            .split(/,\s*/)
            .map((p) => `[${p.trim()}]`)
            .join("");
        })
        .replace(/\*\*(.+?)\*\*/g, "⟦BOLD⟧$1⟦/BOLD⟧")
        .replace(/\*([^*\s][^*]*?)\*/g, "⟦ITALIC⟧$1⟦/ITALIC⟧")
        .replace(/\*/g, "")
        .replace(/⟦BOLD⟧/g, "**")
        .replace(/⟦\/BOLD⟧/g, "**")
        .replace(/⟦ITALIC⟧/g, "*")
        .replace(/⟦\/ITALIC⟧/g, "*");

      const inlineParts: React.ReactNode[] = [];
      const inlinePattern = /(\*\*(.+?)\*\*|\*([^*\s][^*]*?)\*|\[(LOG|TRANS)-(\d+)\]|«(.+?)»)/g;
      let lastIdx = 0;
      let inlineMatch;

      while ((inlineMatch = inlinePattern.exec(processed)) !== null) {
        if (inlineMatch.index > lastIdx) {
          inlineParts.push(
            <span key={`t-${lineIdx}-${lastIdx}`}>
              {processed.slice(lastIdx, inlineMatch.index)}
            </span>,
          );
        }
        if (inlineMatch[6]) {
          inlineParts.push(
            <span
              key={`q-${lineIdx}-${inlineMatch.index}`}
              style={{
                fontStyle: "italic",
                color: "#d4d4d4",
                background: `rgba(${brandRgb}, 0.08)`,
                padding: "1px 4px",
                borderRadius: "3px",
                borderLeft: `2px solid ${brandColor}`,
              }}
            >
              «{inlineMatch[6]}»
            </span>,
          );
        } else if (inlineMatch[2]) {
          inlineParts.push(
            <strong key={`b-${lineIdx}-${inlineMatch.index}`} style={{ color: "#eee" }}>
              {inlineMatch[2]}
            </strong>,
          );
        } else if (inlineMatch[3]) {
          inlineParts.push(
            <em key={`i-${lineIdx}-${inlineMatch.index}`} style={{ color: "#ccc" }}>
              {inlineMatch[3]}
            </em>,
          );
        } else if (inlineMatch[4] && inlineMatch[5]) {
          const citKey = `${inlineMatch[4]}-${inlineMatch[5]}`;
          const cit = citationMap.get(citKey);
          const isLog = inlineMatch[4] === "LOG";
          const isOtf = cit && (cit.type === "OTF" || cit.role === "otf");
          const bgColor = isOtf ? "#06b6d4" : isLog ? "var(--color-data-log, #ff4d8d)" : "#8B5CF6";
          const seqNum = sequentialMap.get(citKey);
          inlineParts.push(
            <sup
              key={`cit-${lineIdx}-${inlineMatch.index}`}
              title={citKey}
              onClick={(e) => {
                if (cit) {
                  const rect = (e.target as HTMLElement).getBoundingClientRect();
                  setActiveCitation({
                    citation: cit,
                    x: rect.left,
                    y: rect.top - 10,
                  });
                }
              }}
              style={{
                background: bgColor,
                color: "#fff",
                padding: "1px 6px",
                borderRadius: "6px",
                fontSize: "0.65rem",
                fontWeight: 700,
                marginLeft: "2px",
                marginRight: "2px",
                display: "inline-block",
                lineHeight: "1.2",
                opacity: seqNum ? 1 : 0.6,
                cursor: "pointer",
                transition: "all 0.2s ease",
                userSelect: "none",
              }}
              onMouseOver={(e) => {
                if (seqNum) {
                  (e.target as HTMLElement).style.transform = "scale(1.15)";
                  (e.target as HTMLElement).style.boxShadow = `0 0 8px ${bgColor}`;
                }
              }}
              onMouseOut={(e) => {
                if (seqNum) {
                  (e.target as HTMLElement).style.transform = "scale(1)";
                  (e.target as HTMLElement).style.boxShadow = "none";
                }
              }}
            >
              {seqNum || citKey}
            </sup>,
          );
        }
        lastIdx = inlineMatch.index + inlineMatch[0].length;
      }
      if (lastIdx < processed.length) {
        inlineParts.push(<span key={`t-${lineIdx}-end`}>{processed.slice(lastIdx)}</span>);
      }

      if (isBullet) {
        elements.push(
          <div
            key={`line-${lineIdx}`}
            style={{
              paddingLeft: "16px",
              margin: "4px 0",
              lineHeight: "1.6",
              position: "relative",
            }}
          >
            <span style={{ position: "absolute", left: "0", color: brandColor }}>•</span>
            {inlineParts}
          </div>,
        );
      } else {
        elements.push(
          <p key={`p-${lineIdx}`} style={{ margin: "4px 0", lineHeight: "1.6" }}>
            {inlineParts}
          </p>,
        );
      }
    });

    return <>{elements}</>;
  };

  // ─── Render metric row ───
  const MetricRow = ({
    label,
    valA,
    valB,
    unit = "",
    highlight = false,
  }: {
    label: string;
    valA: React.ReactNode;
    valB: React.ReactNode;
    unit?: string;
    highlight?: boolean;
  }) => (
    <div
      style={{
        display: "flex",
        padding: "6px 0",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
        fontSize: "0.75rem",
      }}
    >
      <div style={{ flex: 1, color: "#888", fontWeight: 600 }}>{label}</div>
      <div
        style={{
          flex: 1,
          textAlign: "center",
          color: highlight ? "#22c55e" : "#ccc",
          fontWeight: highlight ? 700 : 400,
        }}
      >
        {valA}
        {unit}
      </div>
      <div
        style={{
          flex: 1,
          textAlign: "center",
          color: highlight ? "#22c55e" : "#ccc",
          fontWeight: highlight ? 700 : 400,
        }}
      >
        {valB}
        {unit}
      </div>
    </div>
  );

  // ─── Mode Badge Component ───
  const ModeBadge = ({ mode }: { mode?: string }) => {
    if (!mode || mode === "—") return <span>—</span>;
    const color =
      mode === "RAW"
        ? "#22c55e"
        : mode === "HYBRID"
          ? "#f59e0b"
          : mode === "SUMMARY"
            ? "#ef4444"
            : "#ccc";
    const bg =
      mode === "RAW"
        ? "rgba(34,197,94,0.15)"
        : mode === "HYBRID"
          ? "rgba(245,158,11,0.15)"
          : mode === "SUMMARY"
            ? "rgba(239,68,68,0.15)"
            : "transparent";
    return (
      <span
        style={{
          color,
          background: bg,
          padding: "2px 6px",
          borderRadius: "4px",
          fontSize: "0.65rem",
          fontWeight: 800,
          border: `1px solid ${color}40`,
        }}
      >
        {mode}
      </span>
    );
  };

  return (
    <div
      style={{ display: "flex", height: "100%", gap: "16px", overflow: "hidden" }}
      onClick={() => activeCitation && setActiveCitation(null)}
    >
      {/* ═══ LEFT: History sidebar ═══ */}
      <div
        style={{
          width: "240px",
          minWidth: "240px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          overflow: "hidden",
        }}
      >
        <div style={{ ...cardStyle }}>
          <h4
            style={{
              margin: "0 0 12px 0",
              fontSize: "0.75rem",
              fontWeight: 800,
              letterSpacing: "1px",
              color: "#888",
            }}
          >
            {t("admin.brainPlayground.history")}
          </h4>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              overflowY: "auto",
              maxHeight: "calc(100vh - 300px)",
            }}
          >
            {benchmarks.length === 0 ? (
              <div
                style={{
                  color: "#555",
                  fontSize: "0.75rem",
                  textAlign: "center",
                  padding: "20px 0",
                }}
              >
                {t("admin.brainPlayground.noBenchmarksYet")}
              </div>
            ) : (
              benchmarks.map((b) => (
                <div key={b.id} style={{ position: "relative" }}>
                  <button
                    onClick={() => loadBenchmark(b.id)}
                    style={{
                      textAlign: "left",
                      width: "100%",
                      background:
                        benchmarkId === b.id ? `rgba(${brandRgb}, 0.1)` : "rgba(255,255,255,0.02)",
                      border:
                        benchmarkId === b.id
                          ? `1px solid rgba(${brandRgb}, 0.3)`
                          : "1px solid transparent",
                      borderRadius: "8px",
                      padding: "10px 28px 10px 12px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (benchmarkId !== b.id)
                        e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                    }}
                    onMouseLeave={(e) => {
                      if (benchmarkId !== b.id)
                        e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.73rem",
                        color: "#ccc",
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {b.query.substring(0, 40)}
                      {b.query.length > 40 ? "..." : ""}
                    </div>
                    <div
                      style={{
                        fontSize: "0.65rem",
                        color: "#666",
                        marginTop: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>
                        {new Date(b.created_at).toLocaleDateString("es-ES", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {b.rating_a ? (
                          <StarRating
                            value={b.rating_a}
                            readOnly
                            size="10px"
                            activeColor="#f59e0b"
                          />
                        ) : null}
                        {b.rating_a && b.rating_b ? <span style={{ opacity: 0.5 }}>/</span> : null}
                        {b.rating_b ? (
                          <StarRating
                            value={b.rating_b}
                            readOnly
                            size="10px"
                            activeColor="#f59e0b"
                          />
                        ) : null}
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteBenchmark(b.id);
                    }}
                    title={t("admin.brainPlayground.delete")}
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "6px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#555",
                      fontSize: "0.75rem",
                      padding: "2px",
                      transition: "color 0.2s",
                      lineHeight: 1,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#ef4444")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#555")}
                  >
                    <i className="bx bx-trash"></i>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ═══ MAIN: Query + Results ═══ */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          overflow: "hidden",
        }}
      >
        {/* Query input area */}
        <div style={{ ...cardStyle, flexShrink: 0 }}>
          <div
            style={{ display: "flex", gap: "12px", marginBottom: "12px", alignItems: "flex-end" }}
          >
            {/* Mode toggle */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                alignItems: "center",
                minWidth: "80px",
              }}
            >
              <label
                style={{
                  fontSize: "0.6rem",
                  fontWeight: 800,
                  letterSpacing: "0.5px",
                  color: "#666",
                  marginBottom: "2px",
                }}
              >
                {t("admin.brainPlayground.mode")}
              </label>
              <div
                style={{
                  display: "flex",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.1)",
                  background: "rgba(255,255,255,0.03)",
                }}
              >
                <button
                  onClick={() => setComparisonMode("single")}
                  style={{
                    padding: "6px 10px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    background: isSingle ? `rgba(${brandRgb}, 0.2)` : "transparent",
                    color: isSingle ? brandColor : "#666",
                    transition: "all 0.2s",
                  }}
                >
                  <i className="bx bx-play" style={{ fontSize: "0.8rem" }}></i>
                </button>
                <button
                  onClick={() => setComparisonMode("dual")}
                  style={{
                    padding: "6px 10px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    background: !isSingle ? `rgba(${brandRgb}, 0.2)` : "transparent",
                    color: !isSingle ? brandColor : "#666",
                    transition: "all 0.2s",
                  }}
                >
                  <i className="bx bx-columns" style={{ fontSize: "0.8rem" }}></i>
                </button>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  fontSize: "0.65rem",
                  fontWeight: 800,
                  letterSpacing: "0.5px",
                  color: "#888",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                {isSingle ? "PRESET" : "PRESET A"}
              </label>
              <Select
                value={presetIdA}
                onValueChange={(val) => {
                  if (val) setPresetIdA(val);
                }}
              >
                <SelectTrigger size="sm">
                  <SelectValue placeholder={t("admin.brainPlayground.select")}>
                    {() => {
                      const sel = presets.find((p) => p.id === presetIdA);
                      return sel
                        ? `${sel.name}${sel.is_active ? " (activo)" : ""}`
                        : t("admin.brainPlayground.select");
                    }}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {presets.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                      {p.is_active ? " (activo)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isSingle && (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    paddingBottom: "4px",
                    color: "#555",
                    fontWeight: 800,
                    fontSize: "0.8rem",
                  }}
                >
                  {t("admin.brainPlayground.vs")}
                </div>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      letterSpacing: "0.5px",
                      color: "#888",
                      marginBottom: "4px",
                      display: "block",
                    }}
                  >
                    {t("admin.brainPlayground.presetB")}
                  </label>
                  <Select
                    value={presetIdB}
                    onValueChange={(val) => {
                      if (val) setPresetIdB(val);
                    }}
                  >
                    <SelectTrigger size="sm">
                      <SelectValue placeholder={t("admin.brainPlayground.select")}>
                        {() => {
                          const sel = presets.find((p) => p.id === presetIdB);
                          return sel
                            ? `${sel.name}${sel.is_active ? " (activo)" : ""}`
                            : t("admin.brainPlayground.select");
                        }}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {presets.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                          {p.is_active ? " (activo)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          {testCases.length > 0 && (
            <div style={{ marginBottom: "8px", display: "flex", justifyContent: "flex-end" }}>
              <Select
                value={null}
                onValueChange={(val) => {
                  if (!val) return;
                  const tc = testCases.find((t) => t.id === val);
                  if (tc) {
                    setQuery(tc.query);
                    if (tc.filters) {
                      const f = tc.filters;
                      setSelectAll(f.selectAll !== undefined ? f.selectAll : true);
                      setActiveFilters({
                        rundownIds: f.rundownIds || [],
                        eventIds: f.eventIds || [],
                        storyIds: f.storyIds || [],
                        participantIds: f.participantIds || [],
                        tags: [],
                      });
                      if (f.dataSources) {
                        setDataSources({ ...f.dataSources, otfs: f.dataSources.otfs ?? true });
                      }
                    } else {
                      setSelectAll(true);
                      setActiveFilters({
                        rundownIds: [],
                        eventIds: [],
                        storyIds: [],
                        participantIds: [],
                        tags: [],
                      });
                    }
                  }
                }}
              >
                <SelectTrigger size="sm">
                  <SelectValue placeholder={t("admin.brainPlayground.loadTestSuiteQuestion")} />
                </SelectTrigger>
                <SelectContent>
                  {testCases.map((tc) => (
                    <SelectItem key={tc.id} value={tc.id}>
                      {tc.query.substring(0, 60)}
                      {tc.query.length > 60 ? "..." : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div style={{ display: "flex", gap: "10px" }}>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("admin.brainPlayground.queryPlaceholder")}
              rows={2}
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#fff",
                borderRadius: "8px",
                padding: "10px 14px",
                fontSize: "0.82rem",
                resize: "vertical",
                fontFamily: "inherit",
                outline: "none",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.metaKey) runComparison();
              }}
            />
            <button
              onClick={runComparison}
              disabled={running || !query.trim() || !presetIdA || (!isSingle && !presetIdB)}
              style={{
                background: running ? "rgba(255,255,255,0.05)" : `rgba(${brandRgb}, 0.2)`,
                border: `1px solid ${running ? "rgba(255,255,255,0.1)" : `rgba(${brandRgb}, 0.4)`}`,
                color: running ? "#888" : brandColor,
                borderRadius: "10px",
                padding: "10px 24px",
                fontSize: "0.8rem",
                fontWeight: 800,
                cursor: running ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}
            >
              <i
                className={`bx ${running ? "bx-loader-alt bx-spin" : "bx-play"}`}
                style={{ fontSize: "1.1rem" }}
              ></i>
              {running
                ? t("admin.brainPlayground.executingPipeline")
                : isSingle
                  ? t("admin.brainPlayground.run")
                  : t("admin.brainPlayground.compare")}
            </button>
            {running && (
              <button
                onClick={() => {
                  activeStreamsRef.current.forEach((es) => {
                    try {
                      es.close();
                    } catch {}
                  });
                  activeStreamsRef.current = [];
                  setRunning(false);
                  setStatusMsg(`⛔ ${t("admin.brainPlayground.stoppedByUser")}`);
                }}
                style={{
                  background: "rgba(239, 68, 68, 0.15)",
                  border: "1px solid rgba(239, 68, 68, 0.4)",
                  color: "#ef4444",
                  borderRadius: "10px",
                  padding: "10px 18px",
                  fontSize: "0.8rem",
                  fontWeight: 800,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.2s",
                  whiteSpace: "nowrap",
                }}
              >
                <i className="bx bx-stop" style={{ fontSize: "1.1rem" }}></i>
                {t("admin.brainPlayground.stop")}
              </button>
            )}
            {!running && (resultA || resultB) && (
              <button
                onClick={() => {
                  setResultA(null);
                  setResultB(null);
                  setStatusA([]);
                  setStatusB([]);
                  setStatusMsg(null);
                  setTotalTimeMs(null);
                  setBenchmarkId(null);
                  setRatingA(null);
                  setRatingB(null);
                  setQuery("");
                }}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "#ccc",
                  borderRadius: "10px",
                  padding: "10px 18px",
                  fontSize: "0.8rem",
                  fontWeight: 800,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.2s",
                  whiteSpace: "nowrap",
                }}
              >
                <i className="bx bx-refresh" style={{ fontSize: "1.1rem" }}></i>
                {t("admin.brainPlayground.newTest")}
              </button>
            )}
          </div>
          {statusMsg && (
            <div
              style={{
                marginTop: "8px",
                fontSize: "0.75rem",
                color: statusMsg.startsWith("Error") ? "#ef4444" : "#888",
              }}
            >
              {statusMsg}
            </div>
          )}
        </div>

        {/* ═══ DISCOVERY FILTERS ═══ */}
        <div style={{ ...cardStyle, flexShrink: 0 }}>
          <div
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i
                className={`bx bx-chevron-${showFilters ? "down" : "right"}`}
                style={{ color: brandColor }}
              ></i>
              <span
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 800,
                  letterSpacing: "0.5px",
                  color: "#aaa",
                }}
              >
                {t("admin.brainPlayground.contextFilters")}
              </span>
              <div
                className="data-source-chips"
                style={{ marginLeft: "4px" }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className={`data-source-chip logs ${dataSources.logs ? "active" : ""}`}
                  onClick={() => setDataSources((prev) => ({ ...prev, logs: !prev.logs }))}
                >
                  <i className="bx bx-file-blank"></i> LOGS
                </button>
                <button
                  className={`data-source-chip transcripts ${dataSources.transcripts ? "active" : ""}`}
                  onClick={() =>
                    setDataSources((prev) => ({ ...prev, transcripts: !prev.transcripts }))
                  }
                >
                  <i className="bx bx-microphone"></i> TRANS
                </button>
                <button
                  className={`data-source-chip otfs ${dataSources.otfs ? "active" : ""}`}
                  onClick={() => setDataSources((prev) => ({ ...prev, otfs: !prev.otfs }))}
                  style={
                    dataSources.otfs
                      ? {
                          background: "rgba(6,182,212,0.15)",
                          borderColor: "rgba(6,182,212,0.4)",
                          color: "#06b6d4",
                        }
                      : {}
                  }
                >
                  <i className="bx bx-conversation"></i> OTFs
                </button>
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                className={`reset-filters-btn${selectAll ? " active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectAll(!selectAll);
                  if (!selectAll)
                    setActiveFilters({
                      rundownIds: [],
                      eventIds: [],
                      storyIds: [],
                      participantIds: [],
                      tags: [],
                    });
                }}
                style={
                  selectAll
                    ? {
                        background: "rgba(255, 77, 141, 0.15)",
                        borderColor: "var(--discovery-accent, #ff4d8d)",
                        color: "var(--discovery-accent, #ff4d8d)",
                      }
                    : {}
                }
              >
                <i className={`bx ${selectAll ? "bx-check-circle" : "bx-select-multiple"}`}></i>{" "}
                {selectAll ? "ALL SELECTED" : "SELECT ALL"}
              </button>
              {(activeFilters.rundownIds.length > 0 ||
                activeFilters.storyIds.length > 0 ||
                activeFilters.participantIds.length > 0 ||
                activeFilters.tags.length > 0) && (
                <button
                  className="reset-filters-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveFilters({
                      rundownIds: [],
                      eventIds: [],
                      storyIds: [],
                      participantIds: [],
                      tags: [],
                    });
                    setSelectAll(false);
                  }}
                >
                  <i className="bx bx-x"></i> {t("admin.brainPlayground.reset")}
                </button>
              )}
            </div>
          </div>

          {showFilters && (
            <div
              style={{
                marginTop: "10px",
                display: "flex",
                gap: "16px",
                flexWrap: "wrap",
                maxHeight: "300px",
                overflowY: "auto",
              }}
            >
              {/* Rundowns & Stories Tree */}
              <div style={{ minWidth: "200px", flex: 1 }}>
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 800,
                    letterSpacing: "0.5px",
                    color: "#888",
                    marginBottom: "6px",
                  }}
                >
                  {t("admin.brainPlayground.rundownsStories")}
                </div>
                <div className="tree-container">
                  {filtersData.rundowns.map((r) => {
                    const isRundownActive = activeFilters.rundownIds.includes(r.id);
                    return (
                      <div key={r.id} className="tree-item">
                        <div
                          className={`tree-row tree-row-rundown${isRundownActive ? " tree-row-active" : ""}`}
                          onClick={() => toggleRundownExpand(r.id)}
                        >
                          <i
                            className={`bx bx-chevron-${expandedRundownIds.includes(r.id) ? "down" : "right"} tree-expand-icon`}
                          />
                          <input
                            type="checkbox"
                            className="tree-checkbox tree-checkbox-rundown"
                            checked={isRundownActive}
                            onChange={() => toggleFilter("rundownIds", r.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <i className="bx bx-calendar tree-type-icon tree-icon-rundown" />
                          <span className="tree-label">{r.name}</span>
                        </div>
                        {expandedRundownIds.includes(r.id) && (
                          <div className="tree-children tree-children-rundown">
                            {filtersData.events
                              .filter((e) => e.rundownId === r.id)
                              .map((evt) => {
                                const isEventActive = activeFilters.eventIds.includes(evt.id);
                                return (
                                  <div key={evt.id} className="tree-item">
                                    <div
                                      className={`tree-row tree-row-event${isEventActive ? " tree-row-active" : ""}`}
                                      onClick={() => toggleEventExpand(evt.id)}
                                    >
                                      <i
                                        className={`bx bx-chevron-${expandedEventIds.includes(evt.id) ? "down" : "right"} tree-expand-icon`}
                                      />
                                      <input
                                        type="checkbox"
                                        className="tree-checkbox tree-checkbox-event"
                                        checked={isEventActive}
                                        onChange={() => toggleFilter("eventIds", evt.id)}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <i className="bx bx-bookmark tree-type-icon tree-icon-event" />
                                      <span className="tree-label">{evt.title || evt.name}</span>
                                    </div>
                                    {expandedEventIds.includes(evt.id) && (
                                      <div className="tree-children tree-children-event">
                                        {filtersData.stories
                                          .filter((s) => s.rundownId === evt.id)
                                          .map((st) => {
                                            const isStoryActive = activeFilters.storyIds.includes(
                                              st.id,
                                            );
                                            return (
                                              <div
                                                key={st.id}
                                                className={`tree-row tree-row-story${isStoryActive ? " tree-row-active" : ""}`}
                                              >
                                                <div style={{ width: "14px" }} />
                                                <input
                                                  type="checkbox"
                                                  className="tree-checkbox tree-checkbox-story"
                                                  checked={isStoryActive}
                                                  onChange={() => toggleFilter("storyIds", st.id)}
                                                />
                                                <i className="bx bx-book-open tree-type-icon tree-icon-story" />
                                                <span className="tree-label">{st.title}</span>
                                              </div>
                                            );
                                          })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Participants */}
              {filtersData.participants.length > 0 && (
                <div style={{ minWidth: "160px" }}>
                  <div
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      letterSpacing: "0.5px",
                      color: "#888",
                      marginBottom: "6px",
                    }}
                  >
                    {t("admin.brainPlayground.participants")}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    {filtersData.participants.map((p) => {
                      const isActive = activeFilters.participantIds.includes(p.id);
                      return (
                        <div
                          key={p.id}
                          onClick={() => toggleFilter("participantIds", p.id)}
                          style={{
                            cursor: "pointer",
                            opacity: isActive ? 1 : 0.7,
                            transform: isActive ? "scale(1.05)" : "scale(1)",
                            transition: "all 0.2s",
                          }}
                        >
                          <ParticipantBadge
                            participant={p}
                            variant="pill"
                            size="sm"
                            isActive={isActive}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tags & Cameras */}
              {filtersData.tags.length > 0 && (
                <div style={{ minWidth: "160px" }}>
                  <div
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      letterSpacing: "0.5px",
                      color: "#888",
                      marginBottom: "6px",
                    }}
                  >
                    {t("admin.brainPlayground.camerasTags")}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    <PlaygroundTagCloud
                      tags={filtersData.tags}
                      activeIds={activeFilters.tags}
                      onToggle={(id) => toggleFilter("tags", id)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Active Filters Preview Box */}
        <div
          style={{
            marginTop: "15px",
            padding: "12px 16px",
            background: "var(--color-bg-tertiary, rgba(20,20,25,0.95))",
            borderTop: "1px solid var(--color-border, rgba(255,255,255,0.05))",
            borderBottom: "1px solid var(--color-border, rgba(255,255,255,0.05))",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              color: "#888",
              fontSize: "0.75rem",
              fontWeight: 600,
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
              <i
                className="bx bx-filter-alt"
                style={{ color: "var(--color-accent-success, #10b981)" }}
              ></i>
              <span>{t("admin.brainPlayground.activeFilters")}</span>
              {!(
                activeFilters.rundownIds.length > 0 ||
                activeFilters.eventIds.length > 0 ||
                activeFilters.storyIds.length > 0 ||
                activeFilters.participantIds.length > 0 ||
                activeFilters.tags.length > 0
              ) && (
                <span
                  style={{ opacity: 0.5, marginLeft: "4px", fontStyle: "italic", fontWeight: 400 }}
                >
                  {t("admin.brainPlayground.noFiltersApplied")}
                </span>
              )}
            </div>

            {(activeFilters.rundownIds.length > 0 ||
              activeFilters.eventIds.length > 0 ||
              activeFilters.storyIds.length > 0 ||
              activeFilters.participantIds.length > 0 ||
              activeFilters.tags.length > 0) && (
              <button
                onClick={handleLoadPreview}
                style={{
                  fontSize: "0.65rem",
                  padding: "3px 10px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  background: "rgba(139,92,246,0.15)",
                  border: "1px solid rgba(139,92,246,0.4)",
                  color: "#a78bfa",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "rgba(139,92,246,0.25)")}
                onMouseOut={(e) => (e.currentTarget.style.background = "rgba(139,92,246,0.15)")}
              >
                <i className="bx bx-search-alt-2"></i> {t("admin.brainPlayground.contextPreview")}
              </button>
            )}
          </div>
          {(activeFilters.rundownIds.length > 0 ||
            activeFilters.eventIds.length > 0 ||
            activeFilters.storyIds.length > 0 ||
            activeFilters.participantIds.length > 0 ||
            activeFilters.tags.length > 0) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {activeFilters.rundownIds.map((id) => {
                const r = filtersData.rundowns.find((x) => x.id === id);
                return r ? (
                  <span
                    key={`ar-${id}`}
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "#f59e0b",
                      background: "rgba(245,158,11,0.15)",
                      border: "1px solid rgba(245,158,11,0.4)",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    {r.name}
                  </span>
                ) : null;
              })}
              {activeFilters.eventIds.map((id) => {
                const e = filtersData.events.find((x) => x.id === id);
                return e ? (
                  <span
                    key={`ae-${id}`}
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "#38bdf8",
                      background: "rgba(56,189,248,0.15)",
                      border: "1px solid rgba(56,189,248,0.4)",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    {e.title || e.name}
                  </span>
                ) : null;
              })}
              {activeFilters.storyIds.map((id) => {
                const s = filtersData.stories.find((x) => x.id === id);
                return s ? (
                  <span
                    key={`as-${id}`}
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "#10b981",
                      background: "rgba(16,185,129,0.15)",
                      border: "1px solid rgba(16,185,129,0.4)",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    {s.title}
                  </span>
                ) : null;
              })}
              {activeFilters.participantIds.map((id) => {
                const p = filtersData.participants.find((x) => x.id === id);
                return p ? (
                  <span
                    key={`ap-${id}`}
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "#ec4899",
                      background: "rgba(236,72,153,0.15)",
                      border: "1px solid rgba(236,72,153,0.4)",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    {p.alias || p.name}
                  </span>
                ) : null;
              })}
              {activeFilters.tags.map((id) => {
                const t = filtersData.tags.find((x) => x.id === id);
                return t ? (
                  <span
                    key={`at-${id}`}
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "#eab308",
                      background: "rgba(234,179,8,0.15)",
                      border: "1px solid rgba(234,179,8,0.4)",
                      padding: "3px 8px",
                      borderRadius: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    {t.name}
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>
      </div>

      {/* Results side by side */}
      {(resultA || resultB || running) && (
        <div style={{ flex: 1, display: "flex", gap: "16px", overflow: "hidden" }}>
          {/* Result A */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                ...cardStyle,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 800,
                    letterSpacing: "1px",
                    color: isSingle ? brandColor : "#A3E635",
                  }}
                >
                  {isSingle ? "RESULT" : t("admin.brainPlayground.presetA")}
                </div>
                <div
                  style={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff", marginTop: "2px" }}
                >
                  {getPresetName(presetIdA)}
                </div>
              </div>
              {benchmarkId && (
                <StarRating value={ratingA || 0} onChange={(v) => updateRating("a", v)} />
              )}
            </div>
            <div
              style={{
                ...cardStyle,
                flex: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {statusA.length > 0 && !resultA?.answer && !resultA?.error && (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {statusA.map((msg, i) => {
                    const isModo = msg.includes("Modo final:");
                    const isLast = i === statusA.length - 1;
                    return (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "0.7rem",
                          color: isLast ? "#ddd" : "#666",
                        }}
                      >
                        {isModo ? null : isLast && running ? (
                          <i className="bx bx-loader-alt bx-spin" style={{ color: "#A3E635" }}></i>
                        ) : (
                          <i className="bx bx-check" style={{ color: "#A3E635" }}></i>
                        )}
                        {isModo ? (
                          <>
                            Modo final:{" "}
                            <ModeBadge
                              mode={msg
                                .split("Modo final:")[1]
                                ?.trim()
                                ?.replace(/[^a-zA-Z_]/g, "")}
                            />
                          </>
                        ) : (
                          msg
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {resultA?.error ? (
                <div style={{ color: "#ef4444", fontSize: "0.8rem" }}>Error: {resultA.error}</div>
              ) : resultA?.answer ? (
                <div
                  style={{
                    color: "#ddd",
                    fontSize: "0.8rem",
                    lineHeight: "1.6",
                    wordBreak: "break-word",
                  }}
                >
                  {renderFormattedAnswer(resultA.answer, resultA.citations)}
                  {running && !resultA.error && (
                    <i
                      className="bx bx-loader-alt bx-spin"
                      style={{ marginLeft: "8px", color: "#A3E635", fontSize: "0.9rem" }}
                    ></i>
                  )}
                </div>
              ) : null}
              {!resultA && statusA.length === 0 && !running && (
                <div style={{ color: "#666", fontSize: "0.8rem" }}>
                  {t("admin.brainPlayground.noResponse")}
                </div>
              )}
            </div>
            {/* Context Inspector A */}
            {resultA?.metadata?.contextDebug && (
              <div style={{ ...cardStyle, flexShrink: 0 }}>
                <div
                  onClick={() => setInspectorOpenA(!inspectorOpenA)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <i
                      className="bx bx-search-alt"
                      style={{ fontSize: "0.9rem", color: "#A3E635" }}
                    ></i>
                    <span
                      style={{
                        fontSize: "0.68rem",
                        fontWeight: 800,
                        letterSpacing: "0.5px",
                        color: "#888",
                      }}
                    >
                      {t("admin.brainPlayground.contextInspector")}
                    </span>
                    <span
                      style={{
                        fontSize: "0.6rem",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        background:
                          resultA.metadata.contextDebug.contextMode === "RAW"
                            ? "rgba(34,197,94,0.15)"
                            : resultA.metadata.contextDebug.contextMode === "HYBRID"
                              ? "rgba(139,92,246,0.15)"
                              : "rgba(245,158,11,0.15)",
                        color:
                          resultA.metadata.contextDebug.contextMode === "RAW"
                            ? "#22c55e"
                            : resultA.metadata.contextDebug.contextMode === "HYBRID"
                              ? "#8b5cf6"
                              : "#f59e0b",
                        fontWeight: 700,
                      }}
                    >
                      {resultA.metadata.contextDebug.contextMode}
                    </span>
                    <span style={{ fontSize: "0.6rem", color: "#666" }}>
                      {resultA.metadata.contextDebug.contextTokens
                        ? `~${(resultA.metadata.contextDebug.contextTokens / 1000).toFixed(1)}k tokens`
                        : `${resultA.metadata.contextDebug.contextSizeKB}KB`}
                    </span>
                  </div>
                  <i
                    className={`bx bx-chevron-${inspectorOpenA ? "down" : "right"}`}
                    style={{ fontSize: "0.9rem", color: "#666" }}
                  ></i>
                </div>
                {inspectorOpenA && (
                  <div
                    style={{
                      marginTop: "10px",
                      fontSize: "0.7rem",
                      maxHeight: "400px",
                      overflowY: "auto",
                    }}
                  >
                    {/* Context size bar */}
                    <div style={{ marginBottom: "10px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                          color: "#888",
                        }}
                      >
                        <span>
                          {t("admin.brainPlayground.contextTokens", {
                            count: resultA.metadata.contextDebug.contextTokens
                              ? (resultA.metadata.contextDebug.contextTokens / 1000).toFixed(1)
                              : resultA.metadata.contextDebug.contextSizeKB || 0,
                          })}
                        </span>
                        <span>
                          {resultA.metadata.contextDebug.logCount} logs +{" "}
                          {resultA.metadata.contextDebug.transcriptCount} trans
                        </span>
                      </div>
                      <div
                        style={{
                          height: "4px",
                          borderRadius: "2px",
                          background: "rgba(255,255,255,0.06)",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            borderRadius: "2px",
                            width: `${Math.min(100, (resultA.metadata.contextDebug.contextTokens || (resultA.metadata.contextDebug.contextSizeKB || 0) * 293 || 0) / 10000)}%`,
                            background: "linear-gradient(90deg, #22c55e, #f59e0b, #ef4444)",
                          }}
                        />
                      </div>
                    </div>
                    {/* Stories */}
                    <div style={{ marginBottom: "6px", color: "#888", fontWeight: 700 }}>
                      {t("admin.brainPlayground.storiesIncluded", {
                        count: resultA.metadata.contextDebug.storiesIncluded?.length || 0,
                      })}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px",
                        marginBottom: "10px",
                      }}
                    >
                      {resultA.metadata.contextDebug.storiesIncluded?.map((s, i) => (
                        <span
                          key={i}
                          style={{
                            fontSize: "0.62rem",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            background: s.isOTF ? "rgba(139,92,246,0.1)" : "rgba(255,255,255,0.04)",
                            border: `1px solid ${s.isOTF ? "rgba(139,92,246,0.2)" : "rgba(255,255,255,0.06)"}`,
                            color: s.isOTF ? "#a78bfa" : "#aaa",
                          }}
                        >
                          {s.title}
                          {s.isOTF ? " (OTF)" : ""}
                        </span>
                      ))}
                    </div>
                    {(resultA.metadata.contextDebug.storiesExcluded?.length || 0) > 0 && (
                      <>
                        <div style={{ marginBottom: "6px", color: "#666", fontWeight: 700 }}>
                          {t("admin.brainPlayground.storiesExcluded", {
                            count: resultA.metadata.contextDebug.storiesExcluded?.length,
                          })}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                            marginBottom: "10px",
                          }}
                        >
                          {resultA.metadata.contextDebug.storiesExcluded
                            ?.slice(0, 15)
                            .map((s, i) => (
                              <span
                                key={i}
                                style={{
                                  fontSize: "0.6rem",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  background: "rgba(239,68,68,0.05)",
                                  border: "1px solid rgba(239,68,68,0.1)",
                                  color: "#888",
                                  textDecoration: "line-through",
                                }}
                              >
                                {s.title}
                              </span>
                            ))}
                        </div>
                      </>
                    )}
                    {/* Context preview */}
                    <details style={{ marginTop: "6px" }}>
                      <summary
                        style={{
                          cursor: "pointer",
                          color: "#666",
                          fontSize: "0.65rem",
                          fontWeight: 700,
                        }}
                      >
                        {t("admin.brainPlayground.contextPreviewLabel")}
                      </summary>
                      <pre
                        style={{
                          marginTop: "6px",
                          padding: "8px",
                          borderRadius: "6px",
                          background: "rgba(0,0,0,0.3)",
                          color: "#888",
                          fontSize: "0.6rem",
                          lineHeight: "1.4",
                          maxHeight: "500px",
                          overflow: "auto",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {resultA.metadata.contextDebug.contextPreview}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Result B */}
          {!isSingle && (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  ...cardStyle,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      letterSpacing: "1px",
                      color: "#10b981",
                    }}
                  >
                    PRESET B
                  </div>
                  <div
                    style={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff", marginTop: "2px" }}
                  >
                    {getPresetName(presetIdB)}
                  </div>
                </div>
                {benchmarkId && (
                  <StarRating value={ratingB || 0} onChange={(v) => updateRating("b", v)} />
                )}
              </div>
              <div
                style={{
                  ...cardStyle,
                  flex: 1,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {statusB.length > 0 && !resultB?.answer && !resultB?.error && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {statusB.map((msg, i) => {
                      const isModo = msg.includes("Modo final:");
                      const isLast = i === statusB.length - 1;
                      return (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "0.7rem",
                            color: isLast ? "#ddd" : "#666",
                          }}
                        >
                          {isModo ? null : isLast && running ? (
                            <i
                              className="bx bx-loader-alt bx-spin"
                              style={{ color: "#10b981" }}
                            ></i>
                          ) : (
                            <i className="bx bx-check" style={{ color: "#10b981" }}></i>
                          )}
                          {isModo ? (
                            <>
                              Modo final:{" "}
                              <ModeBadge
                                mode={msg
                                  .split("Modo final:")[1]
                                  ?.trim()
                                  ?.replace(/[^a-zA-Z_]/g, "")}
                              />
                            </>
                          ) : (
                            msg
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {resultB?.error ? (
                  <div style={{ color: "#ef4444", fontSize: "0.8rem" }}>Error: {resultB.error}</div>
                ) : resultB?.answer ? (
                  <div
                    style={{
                      color: "#ddd",
                      fontSize: "0.8rem",
                      lineHeight: "1.6",
                      wordBreak: "break-word",
                    }}
                  >
                    {renderFormattedAnswer(resultB.answer, resultB.citations)}
                    {running && !resultB.error && (
                      <i
                        className="bx bx-loader-alt bx-spin"
                        style={{ marginLeft: "8px", color: "#10b981", fontSize: "0.9rem" }}
                      ></i>
                    )}
                  </div>
                ) : null}
                {!resultB && statusB.length === 0 && !running && (
                  <div style={{ color: "#666", fontSize: "0.8rem" }}>
                    {t("admin.brainPlayground.noResponse")}
                  </div>
                )}
              </div>
              {/* Context Inspector B */}
              {resultB?.metadata?.contextDebug && (
                <div style={{ ...cardStyle, flexShrink: 0 }}>
                  <div
                    onClick={() => setInspectorOpenB(!inspectorOpenB)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <i
                        className="bx bx-search-alt"
                        style={{ fontSize: "0.9rem", color: "#10b981" }}
                      ></i>
                      <span
                        style={{
                          fontSize: "0.68rem",
                          fontWeight: 800,
                          letterSpacing: "0.5px",
                          color: "#888",
                        }}
                      >
                        {t("admin.brainPlayground.contextInspector")}
                      </span>
                      <span
                        style={{
                          fontSize: "0.6rem",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          background:
                            resultB.metadata.contextDebug.contextMode === "RAW"
                              ? "rgba(34,197,94,0.15)"
                              : resultB.metadata.contextDebug.contextMode === "HYBRID"
                                ? "rgba(139,92,246,0.15)"
                                : "rgba(245,158,11,0.15)",
                          color:
                            resultB.metadata.contextDebug.contextMode === "RAW"
                              ? "#22c55e"
                              : resultB.metadata.contextDebug.contextMode === "HYBRID"
                                ? "#8b5cf6"
                                : "#f59e0b",
                          fontWeight: 700,
                        }}
                      >
                        {resultB.metadata.contextDebug.contextMode}
                      </span>
                      <span style={{ fontSize: "0.6rem", color: "#666" }}>
                        {resultB.metadata.contextDebug.contextTokens
                          ? `~${(resultB.metadata.contextDebug.contextTokens / 1000).toFixed(1)}k tokens`
                          : `${resultB.metadata.contextDebug.contextSizeKB}KB`}
                      </span>
                    </div>
                    <i
                      className={`bx bx-chevron-${inspectorOpenB ? "down" : "right"}`}
                      style={{ fontSize: "0.9rem", color: "#666" }}
                    ></i>
                  </div>
                  {inspectorOpenB && (
                    <div
                      style={{
                        marginTop: "10px",
                        fontSize: "0.7rem",
                        maxHeight: "400px",
                        overflowY: "auto",
                      }}
                    >
                      <div style={{ marginBottom: "10px" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "4px",
                            color: "#888",
                          }}
                        >
                          <span>
                            {t("admin.brainPlayground.contextTokens", {
                              count: resultB.metadata.contextDebug.contextTokens
                                ? (resultB.metadata.contextDebug.contextTokens / 1000).toFixed(1)
                                : resultB.metadata.contextDebug.contextSizeKB || 0,
                            })}
                          </span>
                          <span>
                            {resultB.metadata.contextDebug.logCount} logs +{" "}
                            {resultB.metadata.contextDebug.transcriptCount} trans
                          </span>
                        </div>
                        <div
                          style={{
                            height: "4px",
                            borderRadius: "2px",
                            background: "rgba(255,255,255,0.06)",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              borderRadius: "2px",
                              width: `${Math.min(100, (resultB.metadata.contextDebug.contextTokens || (resultB.metadata.contextDebug.contextSizeKB || 0) * 293 || 0) / 10000)}%`,
                              background: "linear-gradient(90deg, #22c55e, #f59e0b, #ef4444)",
                            }}
                          />
                        </div>
                      </div>
                      <div style={{ marginBottom: "6px", color: "#888", fontWeight: 700 }}>
                        {t("admin.brainPlayground.storiesIncluded", {
                          count: resultB.metadata.contextDebug.storiesIncluded?.length || 0,
                        })}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                          marginBottom: "10px",
                        }}
                      >
                        {resultB.metadata.contextDebug.storiesIncluded?.map((s, i) => (
                          <span
                            key={i}
                            style={{
                              fontSize: "0.62rem",
                              padding: "2px 6px",
                              borderRadius: "4px",
                              background: s.isOTF
                                ? "rgba(16,185,129,0.1)"
                                : "rgba(255,255,255,0.04)",
                              border: `1px solid ${s.isOTF ? "rgba(16,185,129,0.2)" : "rgba(255,255,255,0.06)"}`,
                              color: s.isOTF ? "#34d399" : "#aaa",
                            }}
                          >
                            {s.title}
                            {s.isOTF ? " (OTF)" : ""}
                          </span>
                        ))}
                      </div>
                      {(resultB.metadata.contextDebug.storiesExcluded?.length || 0) > 0 && (
                        <>
                          <div style={{ marginBottom: "6px", color: "#666", fontWeight: 700 }}>
                            {t("admin.brainPlayground.storiesExcluded", {
                              count: resultB.metadata.contextDebug.storiesExcluded?.length,
                            })}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "4px",
                              marginBottom: "10px",
                            }}
                          >
                            {resultB.metadata.contextDebug.storiesExcluded
                              ?.slice(0, 15)
                              .map((s, i) => (
                                <span
                                  key={i}
                                  style={{
                                    fontSize: "0.6rem",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    background: "rgba(239,68,68,0.05)",
                                    border: "1px solid rgba(239,68,68,0.1)",
                                    color: "#888",
                                    textDecoration: "line-through",
                                  }}
                                >
                                  {s.title}
                                </span>
                              ))}
                          </div>
                        </>
                      )}
                      <details style={{ marginTop: "6px" }}>
                        <summary
                          style={{
                            cursor: "pointer",
                            color: "#666",
                            fontSize: "0.65rem",
                            fontWeight: 700,
                          }}
                        >
                          {t("admin.brainPlayground.contextPreviewLabel")}
                        </summary>
                        <pre
                          style={{
                            marginTop: "6px",
                            padding: "8px",
                            borderRadius: "6px",
                            background: "rgba(0,0,0,0.3)",
                            color: "#888",
                            fontSize: "0.6rem",
                            lineHeight: "1.4",
                            maxHeight: "500px",
                            overflow: "auto",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {resultB.metadata.contextDebug.contextPreview}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Metrics comparison table */}
      {!isSingle && resultA && resultB && !resultA.error && !resultB.error && (
        <div style={{ ...cardStyle, flexShrink: 0 }}>
          <h4
            style={{
              margin: "0 0 10px 0",
              fontSize: "0.7rem",
              fontWeight: 800,
              letterSpacing: "1px",
              color: "#888",
            }}
          >
            {t("admin.brainPlayground.comparativeMetrics")}
          </h4>
          <div
            style={{
              display: "flex",
              padding: "6px 0",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              fontSize: "0.7rem",
              fontWeight: 800,
              color: "#666",
            }}
          >
            <div style={{ flex: 1 }}>{t("admin.brainPlayground.metric")}</div>
            <div style={{ flex: 1, textAlign: "center", color: "#A3E635" }}>
              {t("admin.brainPlayground.presetA")}
            </div>
            <div style={{ flex: 1, textAlign: "center", color: "#10b981" }}>
              {t("admin.brainPlayground.presetB")}
            </div>
          </div>
          <MetricRow
            label={t("admin.brainPlayground.time")}
            valA={
              resultA.metadata?.responseTimeMs
                ? `${(resultA.metadata.responseTimeMs / 1000).toFixed(1)}`
                : "—"
            }
            valB={
              resultB.metadata?.responseTimeMs
                ? `${(resultB.metadata.responseTimeMs / 1000).toFixed(1)}`
                : "—"
            }
            unit="s"
          />
          <MetricRow
            label={t("admin.brainPlayground.inputTokens")}
            valA={
              resultA.metadata?.inputTokens
                ? `${(resultA.metadata.inputTokens / 1000).toFixed(1)}k`
                : "—"
            }
            valB={
              resultB.metadata?.inputTokens
                ? `${(resultB.metadata.inputTokens / 1000).toFixed(1)}k`
                : "—"
            }
          />
          <MetricRow
            label={t("admin.brainPlayground.outputTokens")}
            valA={
              resultA.metadata?.outputTokens
                ? `${(resultA.metadata.outputTokens / 1000).toFixed(1)}k`
                : "—"
            }
            valB={
              resultB.metadata?.outputTokens
                ? `${(resultB.metadata.outputTokens / 1000).toFixed(1)}k`
                : "—"
            }
          />
          <MetricRow
            label={t("admin.brainPlayground.cost")}
            valA={resultA.metadata?.cost ? `$${resultA.metadata.cost.toFixed(4)}` : "—"}
            valB={resultB.metadata?.cost ? `$${resultB.metadata.cost.toFixed(4)}` : "—"}
            highlight
          />
          <MetricRow
            label={t("admin.brainPlayground.citations")}
            valA={resultA.citations?.length || 0}
            valB={resultB.citations?.length || 0}
          />
          <MetricRow
            label={t("admin.brainPlayground.stories")}
            valA={
              resultA.metadata?.searchPipeline
                ? `${resultA.metadata.searchPipeline.storiesUsed || 0}/${resultA.metadata.searchPipeline.totalStoriesAvailable || 0}`
                : "—"
            }
            valB={
              resultB.metadata?.searchPipeline
                ? `${resultB.metadata.searchPipeline.storiesUsed || 0}/${resultB.metadata.searchPipeline.totalStoriesAvailable || 0}`
                : "—"
            }
          />
          <MetricRow
            label={t("admin.brainPlayground.mode")}
            valA={<ModeBadge mode={resultA.metadata?.searchPipeline?.mode} />}
            valB={<ModeBadge mode={resultB.metadata?.searchPipeline?.mode} />}
          />
          <MetricRow
            label={t("admin.brainPlayground.logsTrans")}
            valA={
              resultA.metadata?.components
                ? `${resultA.metadata.components.logs || 0} + ${resultA.metadata.components.transcriptions || 0}`
                : "—"
            }
            valB={
              resultB.metadata?.components
                ? `${resultB.metadata.components.logs || 0} + ${resultB.metadata.components.transcriptions || 0}`
                : "—"
            }
          />
          {totalTimeMs && (
            <div
              style={{ marginTop: "8px", fontSize: "0.7rem", color: "#555", textAlign: "right" }}
            >
              {t("admin.brainPlayground.totalTimeAB", { time: (totalTimeMs / 1000).toFixed(1) })}
            </div>
          )}
        </div>
      )}

      {/* Single-mode metrics panel */}
      {isSingle && resultA && !resultA.error && (
        <div style={{ ...cardStyle, flexShrink: 0 }}>
          <h4
            style={{
              margin: "0 0 10px 0",
              fontSize: "0.7rem",
              fontWeight: 800,
              letterSpacing: "1px",
              color: "#888",
            }}
          >
            {t("admin.brainPlayground.executionMetrics")}
          </h4>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "10px",
            }}
          >
            {resultA.metadata?.responseTimeMs && (
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    color: "#666",
                    letterSpacing: "0.5px",
                    marginBottom: "4px",
                  }}
                >
                  {t("admin.brainPlayground.time")}
                </div>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#fff" }}>
                  {(resultA.metadata.responseTimeMs / 1000).toFixed(1)}
                  <span style={{ fontSize: "0.7rem", color: "#888" }}>s</span>
                </div>
              </div>
            )}
            {resultA.metadata?.cost !== undefined && (
              <div
                style={{
                  background: "rgba(34,197,94,0.06)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    color: "#666",
                    letterSpacing: "0.5px",
                    marginBottom: "4px",
                  }}
                >
                  {t("admin.brainPlayground.cost")}
                </div>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#22c55e" }}>
                  ${resultA.metadata.cost.toFixed(4)}
                </div>
              </div>
            )}
            {resultA.metadata?.inputTokens && (
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    color: "#666",
                    letterSpacing: "0.5px",
                    marginBottom: "4px",
                  }}
                >
                  {t("admin.brainPlayground.inputTokens")}
                </div>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#fff" }}>
                  {(resultA.metadata.inputTokens / 1000).toFixed(1)}
                  <span style={{ fontSize: "0.7rem", color: "#888" }}>k</span>
                </div>
              </div>
            )}
            {resultA.metadata?.outputTokens && (
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    color: "#666",
                    letterSpacing: "0.5px",
                    marginBottom: "4px",
                  }}
                >
                  {t("admin.brainPlayground.outputTokens")}
                </div>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#fff" }}>
                  {(resultA.metadata.outputTokens / 1000).toFixed(1)}
                  <span style={{ fontSize: "0.7rem", color: "#888" }}>k</span>
                </div>
              </div>
            )}
            {resultA.citations && (
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    color: "#666",
                    letterSpacing: "0.5px",
                    marginBottom: "4px",
                  }}
                >
                  {t("admin.brainPlayground.citations")}
                </div>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#fff" }}>
                  {resultA.citations.length}
                </div>
              </div>
            )}
            {resultA.metadata?.searchPipeline && (
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    color: "#666",
                    letterSpacing: "0.5px",
                    marginBottom: "4px",
                  }}
                >
                  {t("admin.brainPlayground.stories")}
                </div>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#fff" }}>
                  {resultA.metadata.searchPipeline.storiesUsed || 0}
                  <span style={{ fontSize: "0.7rem", color: "#888" }}>
                    /{resultA.metadata.searchPipeline.totalStoriesAvailable || 0}
                  </span>
                </div>
              </div>
            )}
            {resultA.metadata?.searchPipeline?.mode && (
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    color: "#666",
                    letterSpacing: "0.5px",
                    marginBottom: "4px",
                  }}
                >
                  {t("admin.brainPlayground.mode")}
                </div>
                <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#fff" }}>
                  <ModeBadge mode={resultA.metadata.searchPipeline.mode} />
                </div>
              </div>
            )}
            {resultA.metadata?.components && (
              <div
                style={{
                  background: "rgba(255,255,255,0.03)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    color: "#666",
                    letterSpacing: "0.5px",
                    marginBottom: "4px",
                  }}
                >
                  {t("admin.brainPlayground.logsTrans")}
                </div>
                <div style={{ fontSize: "1rem", fontWeight: 700, color: "#fff" }}>
                  {resultA.metadata.components.logs || 0}{" "}
                  <span style={{ fontSize: "0.7rem", color: "#888" }}>+</span>{" "}
                  {resultA.metadata.components.transcriptions || 0}
                </div>
              </div>
            )}
          </div>
          {/* Pipeline Steps */}
          {resultA.metadata?.searchPipeline?.steps &&
            resultA.metadata.searchPipeline.steps.length > 0 && (
              <details style={{ marginTop: "12px" }}>
                <summary
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 800,
                    color: "#666",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  {t("admin.brainPlayground.pipelineSteps", {
                    count: resultA.metadata.searchPipeline.steps.length,
                  })}
                </summary>
                <div
                  style={{
                    marginTop: "6px",
                    maxHeight: "200px",
                    overflowY: "auto",
                    background: "rgba(0,0,0,0.2)",
                    borderRadius: "6px",
                    padding: "6px 8px",
                  }}
                >
                  {resultA.metadata.searchPipeline.steps.map(
                    (step: { ts: number; msg: string }, i: number) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          gap: "8px",
                          fontSize: "0.6rem",
                          lineHeight: 1.6,
                          color: "#aaa",
                          borderBottom: "1px solid rgba(255,255,255,0.03)",
                          padding: "2px 0",
                        }}
                      >
                        <span
                          style={{
                            color: "#555",
                            fontFamily: "monospace",
                            flexShrink: 0,
                            minWidth: "36px",
                          }}
                        >
                          {(step.ts / 1000).toFixed(1)}s
                        </span>
                        <span>{step.msg}</span>
                      </div>
                    ),
                  )}
                </div>
              </details>
            )}
          {totalTimeMs && (
            <div
              style={{ marginTop: "8px", fontSize: "0.7rem", color: "#555", textAlign: "right" }}
            >
              {t("admin.brainPlayground.totalPipelineTime", {
                time: (totalTimeMs / 1000).toFixed(1),
              })}
            </div>
          )}
        </div>
      )}
      {/* Citation Source Popup */}
      {activeCitation &&
        (() => {
          const isTrans =
            activeCitation.citation.role === "transcription" ||
            activeCitation.citation.type === "TRANS";
          const isOtf =
            activeCitation.citation.role === "otf" || activeCitation.citation.type === "OTF";

          // Determine theme color based on type
          let themeColor = "#ff4d8d"; // Default LOG pink
          let bgThemeColor = "rgba(255, 77, 141, 0.08)";
          let titleText = "LOG DE GRABACIÓN";
          let iconClass = "bx bx-file";

          if (isOtf) {
            themeColor = "#06b6d4"; // Cyan for OTF
            bgThemeColor = "rgba(6, 182, 212, 0.08)";
            titleText = "ENTREVISTA (OTF)";
            iconClass = "bx bx-camera-movie";
          } else if (isTrans) {
            themeColor = "#8B5CF6"; // Purple for TRANS
            bgThemeColor = "rgba(139, 92, 246, 0.08)";
            titleText = "TRANSCRIPCIÓN DE AUDIO";
            iconClass = "bx bx-microphone";
          }

          return (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                left: Math.min(activeCitation.x, window.innerWidth - 340),
                top: Math.max(activeCitation.y - 160, 10),
                width: "320px",
                background: "rgba(30, 30, 40, 0.95)",
                backdropFilter: "blur(16px)",
                border: `1px solid ${themeColor}66`, // 40% opacity hex extension
                borderRadius: "12px",
                padding: "16px",
                zIndex: 9999,
                boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 12px ${themeColor}33`,
                animation: "fadeIn 0.15s ease-out",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: themeColor,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                  }}
                >
                  <i className={iconClass} style={{ marginRight: "4px" }}></i>
                  {titleText}
                </span>
                <span
                  onClick={() => setActiveCitation(null)}
                  style={{ cursor: "pointer", color: "#888", fontSize: "1rem", lineHeight: 1 }}
                >
                  ×
                </span>
              </div>
              {activeCitation.citation.speaker && (
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#eee",
                    fontWeight: 600,
                    marginBottom: "6px",
                  }}
                >
                  {activeCitation.citation.speaker}
                </div>
              )}
              <div style={{ fontSize: "0.7rem", color: "#888", marginBottom: "8px" }}>
                <i className="bx bx-time" style={{ marginRight: "4px" }}></i>
                {activeCitation.citation.tc || "N/A"}
                {activeCitation.citation.endAt && (
                  <span>
                    {" "}
                    → {new Date(activeCitation.citation.endAt).toISOString().substring(11, 19)}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#ccc",
                  lineHeight: "1.5",
                  fontStyle: "italic",
                  maxHeight: "120px",
                  overflowY: "auto",
                  background: bgThemeColor,
                  borderRadius: "8px",
                  padding: "10px",
                }}
              >
                "{activeCitation.citation.text}"
              </div>
              <div
                style={{ fontSize: "0.65rem", color: "#555", marginTop: "8px", textAlign: "right" }}
              >
                {activeCitation.citation.srcKey}
              </div>
            </div>
          );
        })()}

      {/* Preview Drawer */}
      {showPreviewDrawer && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: "450px",
            background: "var(--color-bg-primary, rgba(15,15,20,0.98))",
            borderLeft: "1px solid var(--color-border, rgba(255,255,255,0.1))",
            boxShadow: "-10px 0 30px rgba(0,0,0,0.5)",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            backdropFilter: "blur(10px)",
            transform: "translateX(0)",
            transition: "transform 0.3s ease-in-out",
          }}
        >
          <div
            style={{
              padding: "20px",
              borderBottom: "1px solid var(--color-border, rgba(255,255,255,0.05))",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: "1.2rem", color: "#fff", fontWeight: 700 }}>
                {t("admin.brainPlayground.contextPreview")}
              </h3>
              {previewTokens && (
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#A3E635",
                    marginTop: "4px",
                    fontWeight: 600,
                  }}
                >
                  {t("admin.brainPlayground.estimationTokens", {
                    count: (previewTokens / 1000).toFixed(1),
                  })}
                </div>
              )}
            </div>
            <button
              onClick={() => setShowPreviewDrawer(false)}
              style={{
                background: "none",
                border: "none",
                color: "#888",
                cursor: "pointer",
                fontSize: "1.5rem",
                padding: "4px",
              }}
            >
              <i className="bx bx-x"></i>
            </button>
          </div>
          <div style={{ padding: "20px", flex: 1, overflowY: "auto" }}>
            {previewLoading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  gap: "10px",
                  color: "#A3E635",
                }}
              >
                <i className="bx bx-loader-alt bx-spin" style={{ fontSize: "2rem" }}></i>
                <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                  {t("admin.brainPlayground.estimatingContext")}
                </div>
              </div>
            ) : previewContent ? (
              <pre
                style={{
                  margin: 0,
                  padding: "16px",
                  background: "rgba(0,0,0,0.5)",
                  borderRadius: "8px",
                  color: "#ccc",
                  fontSize: "0.8rem",
                  lineHeight: "1.5",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "var(--font-mono, monospace)",
                  border: "1px solid rgba(255,255,255,0.05)",
                }}
              >
                {previewContent}
              </pre>
            ) : (
              <div
                style={{
                  color: "#666",
                  fontSize: "0.85rem",
                  textAlign: "center",
                  marginTop: "40px",
                }}
              >
                El contexto está vacío para estos filtros.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Tag cloud sub-component (avoids calling useTags inside a callback)
const PlaygroundTagCloud: React.FC<{
  tags: any[];
  activeIds: string[];
  onToggle: (id: string) => void;
}> = ({ tags, activeIds, onToggle }) => {
  const { getTagColor } = useTags();
  return (
    <>
      {tags.map((t) => {
        const isActive = activeIds.includes(t.id);
        const color = t.isCamera ? "#ffaa00" : getTagColor(t.id);
        const textColor = isActive ? (isColorLight(color) ? "#000" : "#fff") : "#fff";
        return (
          <span
            key={t.id}
            onClick={() => onToggle(t.id)}
            style={{
              borderColor: color,
              background: isActive ? color : "transparent",
              color: textColor,
              opacity: isActive ? 1 : 0.7,
              border: `1px solid ${color}`,
              borderRadius: "12px",
              padding: "1px 8px",
              fontSize: "0.65rem",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            {t.label || t.name || t.id}
          </span>
        );
      })}
    </>
  );
};
